import React from 'react'
import type { StudentSpecificContextualDataEntry } from '@mission.io/mission-toolkit'
import { useOnClickOutside } from '../../../../../utility'
import {
	InformationFrame,
	TextEventInput,
	MediaEventInput,
	ExtenderFrame,
} from './InformationFrame'
import { MdClose } from 'react-icons/md'

/**
 * A component that displays the full contextual data for a single piece of contextual data. It includes the title, subtitle, text, and media, and the ability to vote on the data. It
 * is made up of sub components from the literacy event component so as to look and feel the same as the literacy event.
 * @param {{}} props
 * @param {() => void} props.onFinishClose - A function to call when the frame finishes closing
 * @param {((vote: number, location: { x: number, y: number }) => mixed) | null} props.onVote - A function to call when the user votes on the importance
 */
export function ExpandedDataView({
	contextualData,
	onFinishClose,
}: {
	contextualData: StudentSpecificContextualDataEntry,
	onFinishClose: () => void,
}): React$Node {
	const [animateClose, setAnimateClose] = React.useState(false)
	const [isMediaOpen, setIsMediaOpen] = React.useState(false)

	const extenderRef = React.useRef()
	let ref: ?{ current: ?HTMLElement }
	const startClose = React.useCallback(
		(node, force?: boolean) => {
			// Force should be true if user is clicking "Exit" within the modal
			if (force) {
				setAnimateClose(true)
				return
			}
			// Check if the clicked on element has the class Media-Modal-overlay
			if (isMediaOpen) {
				return
			}

			if (ref?.current?.contains(node) || extenderRef.current?.contains(node)) {
				return
			}
			setAnimateClose(true)
		},
		[isMediaOpen, ref]
	)
	ref = useOnClickOutside(startClose)
	const controlModal = {
		isOpen: isMediaOpen,
		setIsOpen: setIsMediaOpen,
	}

	if (!contextualData) {
		return null
	}

	const media =
		contextualData.media?.length > 0 ? contextualData?.media[0] : null
	const onlyMedia = media && !contextualData.text.length

	return (
		<div className="absolute bg-black/75 w-full h-full top-0 left-0 z-[1]">
			<div className="relative w-full h-full md:h-3/5 flex p-10">
				<InformationFrame
					contentClassName="flex flex-col pl-2 lg:pl-[12%]"
					isExiting={animateClose}
					onFinishExit={onFinishClose}
					ref={ref}>
					{!onlyMedia ? (
						<>
							<div className="flex w-full justify-between pt-2 px-4">
								<h3 className="text-black">{contextualData.title}</h3>
								<div className="flex space-x-4 self-start">
									<button
										onClick={e => startClose(e, true)}
										className="bg-error hover:bg-error/75 cursor-pointer font-game-headline text-sm text-white rounded-lg px-2 flex gap-2 !w-fit items-center">
										<MdClose />
										Exit
									</button>
								</div>
							</div>
							<div className="prose px-4 font-bold">
								{contextualData.subtitle}
							</div>
							<TextEventInput
								text={contextualData.text}
								className="flex-1 overflow-auto block h-auto"
							/>
						</>
					) : (
						<>
							<div className="flex w-full justify-between pt-2 px-4">
								<h3 className="text-black">{contextualData.title}</h3>
								<div className="flex space-x-4 self-start">
									<button
										onClick={e => startClose(e, true)}
										className="bg-error hover:bg-error/75 cursor-pointer font-game-headline text-sm text-white rounded-lg px-2 flex align-center gap-2 !w-fit">
										<MdClose />
										Exit
									</button>
								</div>
							</div>
							<div className="prose px-4 font-bold">
								{contextualData.subtitle}
							</div>
							<MediaEventInput
								media={contextualData.media[0]}
								className="items-start"
								controlModal={controlModal}
							/>
						</>
					)}
				</InformationFrame>
				{media && !onlyMedia && (
					<ExtenderFrame
						isExiting={animateClose}
						ref={extenderRef}
						media={media}
						controlModal={controlModal}
					/>
				)}
			</div>
		</div>
	)
}
